import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDpdpDeps } from './deps';
import { MY_ERASURE_QUERY_KEY } from './queries';
import type { MyErasure } from './schemas';

/**
 * BP-11 R17's card: the client asks for their own record to be erased.
 *
 * **Why it exists in this shape.** Google Play has required since 2023, and
 * Apple's Guideline 5.1.1(v) since 2022, that an app which lets somebody
 * create an account lets them delete it from inside the app. 26AD's client
 * app creates one on the first OTP verify, so this card is what makes the
 * two listings submittable at all — not a nicety.
 *
 * **It asks; it does not erase.** The api opens a BP-03 `erase` request with
 * rule 8's ninety-day clock, because some of what a salon holds it is
 * required to keep: an invoice is a statutory record under the GST rules and
 * does not vanish because a client asked. The copy says so rather than
 * implying an instant deletion the law would not allow — a promise this
 * screen cannot keep is worse than a slower one it can.
 *
 * **Two taps, deliberately.** The second tap is the only confirmation in the
 * client app, and it is here because this is the only irreversible thing a
 * client can do to themselves.
 */
export function DeleteAccount(): JSX.Element {
  const { t } = useTranslation('dpdp');
  const { api } = useDpdpDeps();
  const queries = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const existing = useQuery({
    queryKey: MY_ERASURE_QUERY_KEY,
    queryFn: () => api.readMyErasure(),
  });

  const ask = useMutation({
    mutationFn: () => api.requestMyErasure(),
    onSuccess: (row: MyErasure) => {
      setConfirming(false);
      queries.setQueryData(MY_ERASURE_QUERY_KEY, row);
    },
  });

  const pending = existing.data ?? null;

  return (
    <section className="rounded border border-destructive/30 p-3" data-testid="delete-account">
      <h2 className="font-semibold">{t('dpdp.erase.title')}</h2>
      <p className="mt-1 text-sm text-foreground">{t('dpdp.erase.body')}</p>
      {pending === null ? (
        <Ask
          confirming={confirming}
          busy={ask.isPending}
          failed={ask.isError}
          onStart={() => {
            setConfirming(true);
          }}
          onConfirm={() => {
            ask.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      ) : (
        <Pending row={pending} />
      )}
    </section>
  );
}

/** The request as it stands, with the date the salon has to answer by. */
function Pending({ row }: { row: MyErasure }): JSX.Element {
  const { t } = useTranslation('dpdp');
  const key = row.days_left < 0 ? 'dpdp.erase.overdue' : 'dpdp.erase.pending';
  return (
    <p className="mt-2 text-sm" role="status" data-testid="erase-pending">
      {t(key, { date: row.requested_at.slice(0, 10), due: row.due_on })}
    </p>
  );
}

/** The button, and the one confirmation behind it. */
function Ask({
  confirming,
  busy,
  failed,
  onStart,
  onConfirm,
  onCancel,
}: {
  confirming: boolean;
  busy: boolean;
  failed: boolean;
  onStart: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation('dpdp');
  return (
    <>
      {failed && (
        <p className="mt-2 text-sm text-destructive" role="alert" data-testid="erase-failed">
          {t('dpdp.erase.failed')}
        </p>
      )}
      {confirming ? (
        <div className="mt-2 space-y-2" data-testid="erase-confirm">
          <p className="text-sm font-semibold">{t('dpdp.erase.confirm')}</p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="erase-confirm-yes"
              disabled={busy}
              className="rounded bg-destructive px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
              onClick={onConfirm}
            >
              {busy ? t('dpdp.erase.asking') : t('dpdp.erase.action')}
            </button>
            <button
              type="button"
              data-testid="erase-confirm-no"
              className="rounded border border-border px-3 py-1 text-sm"
              onClick={onCancel}
            >
              {t('dpdp.erase.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="erase-start"
          className="mt-2 rounded border border-destructive/40 px-3 py-1 text-sm text-destructive"
          onClick={onStart}
        >
          {t('dpdp.erase.action')}
        </button>
      )}
    </>
  );
}
